import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import PasswordInput from "../components/PasswordInput";
import { Utensils, Lock, Unlock, Plus, Trash2, Search, Store, ShoppingCart, DollarSign, MessageCircle } from "lucide-react";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const PLANO_LABELS_VP = {
  trial: "Trial 7d",
  start: "Start",
  pro: "Pro",
  premium: "Premium",
};
const PLANO_COLORS_VP = {
  trial: "bg-amber-100 text-amber-800",
  start: "bg-slate-100 text-slate-700",
  pro: "bg-red-100 text-red-700",
  premium: "bg-purple-100 text-purple-700",
};

const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; } };
const money = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function GestaoVaiPedeAssinantesPage() {
  const [assinantes, setAssinantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", senha: "", loja_nome: "", whatsapp: "", plano: "trial" });
  const [creating, setCreating] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockMotivo, setBlockMotivo] = useState("");
  const [blockSaving, setBlockSaving] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delConfirm, setDelConfirm] = useState("");
  const [whatsOpen, setWhatsOpen] = useState(false);
  const [whatsTarget, setWhatsTarget] = useState(null);
  const [whatsMsg, setWhatsMsg] = useState("");

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/super-admin/vaipede/assinantes`);
      setAssinantes(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar");
    } finally { setLoading(false); }
  };

  const criar = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/super-admin/vaipede/assinantes`, form);
      toast.success("Assinante VAI PEDE criado");
      setCreateOpen(false);
      setForm({ nome: "", email: "", senha: "", loja_nome: "", whatsapp: "", plano: "trial" });
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar");
    } finally { setCreating(false); }
  };

  const bloquear = async () => {
    setBlockSaving(true);
    try {
      await axios.put(`${API}/super-admin/vaipede/assinantes/${blockTarget.id}/status`, {
        bloqueado: !blockTarget.bloqueado, motivo: blockMotivo,
      });
      toast.success(blockTarget.bloqueado ? "Desbloqueado" : "Bloqueado");
      setBlockOpen(false); setBlockTarget(null); setBlockMotivo("");
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally { setBlockSaving(false); }
  };

  const trocarPlano = async (assinante, novoPlano) => {
    try {
      await axios.put(`${API}/super-admin/vaipede/assinantes/${assinante.id}/plano`, { plano: novoPlano });
      toast.success("Plano atualizado");
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const excluir = async () => {
    if (delConfirm !== "EXCLUIR") { toast.error("Digite EXCLUIR para confirmar"); return; }
    try {
      await axios.delete(`${API}/super-admin/vaipede/assinantes/${delTarget.id}`);
      toast.success("Assinante excluído");
      setDelOpen(false); setDelTarget(null); setDelConfirm("");
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const abrirWhats = (a, template) => {
    setWhatsTarget(a);
    const nome = a.nome?.split(" ")[0] || "assinante";
    const loja = a.loja_nome || "sua loja";
    const templates = {
      promo: `Olá ${nome}! 🎁 Novidade no VAI PEDE para ${loja}: promoção especial no plano Pro. Fale comigo para conhecer!`,
      reativacao: `Oi ${nome}! Notamos que sua assinatura do VAI PEDE para ${loja} está inativa. Podemos te ajudar a retomar o acesso agora mesmo?`,
      trial_fim: `Olá ${nome}! Seu trial gratuito do VAI PEDE está acabando. Quer garantir o plano ${a.plano === "trial" ? "Start" : "Pro"} com desconto especial? Me conta que estruturo pra você.`,
      livre: "",
    };
    setWhatsMsg(templates[template] || "");
    setWhatsOpen(true);
  };

  const enviarWhats = () => {
    if (!whatsTarget?.whatsapp) { toast.error("Assinante sem WhatsApp cadastrado"); return; }
    const numero = whatsTarget.whatsapp.replace(/\D/g, "");
    const link = `https://wa.me/${numero}?text=${encodeURIComponent(whatsMsg)}`;
    window.open(link, "_blank", "noopener,noreferrer");
    setWhatsOpen(false);
  };

  const filtrados = assinantes.filter((a) => {
    const q = busca.toLowerCase().trim();
    if (!q) return true;
    return (a.nome || "").toLowerCase().includes(q)
      || (a.email || "").toLowerCase().includes(q)
      || (a.loja_nome || "").toLowerCase().includes(q)
      || (a.whatsapp || "").includes(q);
  });

  const stats = {
    total: assinantes.length,
    trial: assinantes.filter(a => a.plano === "trial").length,
    ativos: assinantes.filter(a => a.assinatura_ativa).length,
    bloqueados: assinantes.filter(a => a.bloqueado).length,
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6" data-testid="gestao-vaipede-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Utensils className="w-6 h-6 text-[#EA1D2C]" />
            Assinantes — VAI PEDE
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Restaurantes e lanchonetes · {stats.total} total · {stats.trial} em trial · {stats.ativos} ativos · {stats.bloqueados} bloqueados
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="btn-novo-vaipede" className="bg-[#EA1D2C] hover:bg-[#c8121f]">
          <Plus className="w-4 h-4 mr-1" /> Novo assinante
        </Button>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <Input placeholder="Buscar por nome, email, loja ou WhatsApp..." value={busca} onChange={(e) => setBusca(e.target.value)} className="pl-9" data-testid="busca-vaipede" />
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500">Carregando...</div>
      ) : filtrados.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Nenhum assinante encontrado.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {filtrados.map((a) => (
            <Card key={a.id} className={a.bloqueado ? "border-red-200" : ""} data-testid={`assinante-vaipede-${a.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold">{a.nome}</div>
                      <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${PLANO_COLORS_VP[a.plano] || "bg-slate-100"}`}>
                        {PLANO_LABELS_VP[a.plano] || a.plano}
                      </span>
                      {a.plano === "trial" && a.trial_dias_restantes != null && (
                        <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${a.trial_expirado ? "bg-red-100 text-red-700" : a.trial_dias_restantes <= 2 ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"}`}>
                          {a.trial_expirado ? "EXPIRADO" : `${a.trial_dias_restantes}d restantes`}
                        </span>
                      )}
                      {a.bloqueado && <span className="px-2 py-0.5 rounded text-[11px] bg-red-100 text-red-700 font-semibold">BLOQUEADO</span>}
                      {a.super_admin && <span className="px-2 py-0.5 rounded text-[11px] bg-amber-100 text-amber-700 font-semibold">SUPER ADMIN</span>}
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">{a.email}</div>
                    {a.loja_nome && <div className="text-xs text-slate-500 flex items-center gap-1"><Store className="w-3 h-3"/> {a.loja_nome}</div>}
                    {a.whatsapp && <div className="text-xs text-emerald-700 font-semibold flex items-center gap-1"><MessageCircle className="w-3 h-3"/> {a.whatsapp}</div>}
                    <div className="text-xs text-slate-500 mt-1">
                      Cadastrado em {fmtDate(a.created_at)}
                      {a.plano === "trial" && a.trial_ate && <> · Trial até {fmtDate(a.trial_ate)}</>}
                      {a.assinatura_iniciada_em && <> · Assinou {fmtDate(a.assinatura_iniciada_em)}</>}
                    </div>
                    <div className="flex gap-3 mt-2 text-xs text-slate-600 flex-wrap">
                      <span className="flex items-center gap-1"><ShoppingCart className="w-3 h-3"/> {a.stats?.pedidos_finalizados || 0}/{a.stats?.pedidos_total || 0} pedidos</span>
                      <span className="flex items-center gap-1"><DollarSign className="w-3 h-3"/> {money(a.stats?.faturamento_total)}</span>
                      <span>Ticket {money(a.stats?.ticket_medio)}</span>
                      <span className="flex items-center gap-1"><Store className="w-3 h-3"/> {a.stats?.mesas || 0} mesas</span>
                      <span>{a.stats?.garcons || 0} garç · {a.stats?.entregadores || 0} entreg</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={a.plano || "trial"}
                      onChange={(e) => trocarPlano(a, e.target.value)}
                      className="text-xs border rounded px-2 py-1"
                      data-testid={`plano-select-vp-${a.id}`}
                    >
                      {Object.entries(PLANO_LABELS_VP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    {a.whatsapp && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" onClick={() => abrirWhats(a, "promo")} data-testid={`btn-promo-${a.id}`}>
                          <MessageCircle className="w-4 h-4 mr-1"/>Promo
                        </Button>
                        {(a.plano === "trial" || !a.assinatura_ativa) && (
                          <Button size="sm" variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50" onClick={() => abrirWhats(a, "reativacao")} data-testid={`btn-reat-${a.id}`}>
                            Reativar
                          </Button>
                        )}
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant={a.bloqueado ? "default" : "outline"}
                      onClick={() => { setBlockTarget(a); setBlockOpen(true); setBlockMotivo(""); }}
                      data-testid={`btn-bloq-vp-${a.id}`}
                    >
                      {a.bloqueado ? <><Unlock className="w-4 h-4 mr-1" />Desbloquear</> : <><Lock className="w-4 h-4 mr-1" />Bloquear</>}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => { setDelTarget(a); setDelOpen(true); setDelConfirm(""); }} data-testid={`btn-excluir-vp-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Criar */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="dialog-criar-vaipede">
          <DialogHeader><DialogTitle>Novo assinante VAI PEDE</DialogTitle><DialogDescription>Crie manualmente um assinante do sistema de gestão para restaurantes.</DialogDescription></DialogHeader>
          <form onSubmit={criar} className="space-y-3">
            <div><Label>Nome do responsável*</Label><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
            <div><Label>Nome da loja</Label><Input value={form.loja_nome} onChange={(e) => setForm({ ...form, loja_nome: e.target.value })} /></div>
            <div><Label>Email*</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>WhatsApp (obrig. para envio de promo)</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="5567991992593" /></div>
            <div><Label>Senha*</Label><PasswordInput required value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} /></div>
            <div>
              <Label>Plano inicial</Label>
              <select value={form.plano} onChange={(e) => setForm({ ...form, plano: e.target.value })} className="w-full border rounded px-3 py-2">
                {Object.entries(PLANO_LABELS_VP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={creating} className="bg-[#EA1D2C] hover:bg-[#c8121f]">{creating ? "Criando..." : "Criar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bloquear */}
      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{blockTarget?.bloqueado ? "Desbloquear" : "Bloquear"} assinante</DialogTitle><DialogDescription>Altere o status de acesso deste assinante.</DialogDescription></DialogHeader>
          <div>
            <div className="text-sm">{blockTarget?.nome} — {blockTarget?.email}</div>
            {!blockTarget?.bloqueado && (
              <div className="mt-3"><Label>Motivo (opcional)</Label><Input value={blockMotivo} onChange={(e) => setBlockMotivo(e.target.value)} /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockOpen(false)}>Cancelar</Button>
            <Button onClick={bloquear} disabled={blockSaving}>{blockSaving ? "Salvando..." : "Confirmar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excluir */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Excluir assinante</DialogTitle><DialogDescription>Ação irreversível — todos os dados do assinante serão apagados.</DialogDescription></DialogHeader>
          <div className="text-sm">
            Todos os dados de <b>{delTarget?.email}</b> (produtos, mesas, pedidos, caixa, garçons, entregadores, cupons) serão removidos permanentemente.
            <div className="mt-3"><Label>Digite EXCLUIR para confirmar</Label>
              <Input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} data-testid="del-confirm-vp-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={excluir}>Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp */}
      <Dialog open={whatsOpen} onOpenChange={setWhatsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-600"/> Enviar mensagem via WhatsApp</DialogTitle>
            <DialogDescription>Para: {whatsTarget?.nome} ({whatsTarget?.whatsapp})</DialogDescription>
          </DialogHeader>
          <textarea
            value={whatsMsg}
            onChange={(e) => setWhatsMsg(e.target.value)}
            rows={5}
            className="w-full border rounded-lg p-3 text-sm resize-none"
            placeholder="Digite a mensagem..."
            data-testid="whats-msg-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatsOpen(false)}>Cancelar</Button>
            <Button onClick={enviarWhats} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-whats-enviar">
              <MessageCircle className="w-4 h-4 mr-1"/>Abrir WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
